"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"
import "./AdminDashboard.css"

const LightSpotAccounts = () => {
  const [accounts, setAccounts] = useState([])
  const [mentors, setMentors] = useState([])
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterLevel, setFilterLevel] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [showAssignForm, setShowAssignForm] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedMentor, setSelectedMentor] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    loadAccounts()
    loadMentors()
  }, [])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadAccounts = async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await apiService.getLightSpotRequests()
      
      // Sort requests by creation date in descending order (newest first)
      const sortedResponse = response.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      )
      
      // Fetch mentor details for each request that has an assigned_mentor
      const accountsWithMentors = await Promise.all(sortedResponse.map(async (account) => {
        if (account.assigned_mentor) {
          try {
            const mentor = await apiService.getMentorById(account.assigned_mentor)
            return {
              ...account,
              mentor: mentor
            }
          } catch (error) {
            console.error('Error fetching mentor details:', error)
            return account
          }
        }
        return account
      }))

      console.log('Accounts with mentor details:', accountsWithMentors)
      setAccounts(accountsWithMentors)
    } catch (error) {
      console.error('Error loading Light Spot accounts:', error)
      setError(error.message || 'Failed to load Light Spot accounts')
    } finally {
      setIsLoading(false)
    }
  }

  const loadMentors = async () => {
    try {
      const response = await apiService.getMentors()
      setMentors(response)
    } catch (error) {
      console.error('Error loading mentors:', error)
      setError(error.message || 'Failed to load mentors')
    }
  }

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value)
  }

  const handleLevelFilterChange = (e) => {
    setFilterLevel(e.target.value)
  }

  const handleMentorChange = (e) => {
    setSelectedMentor(e.target.value)
  }

  const openAssignForm = (account) => {
    setSelectedAccount(account)
    setSelectedMentor("")
    setShowAssignForm(true)
  }

  const assignMentor = async (e) => {
    e.preventDefault()

    if (!selectedMentor) {
      setError("Please select a mentor")
      return
    }

    try {
      setIsLoading(true)
      setError("")
      
      console.log('Starting mentor assignment process:')
      console.log('Selected Account:', selectedAccount)
      console.log('Selected Mentor ID:', selectedMentor)
      
      // First approve the request with the mentor ID
      const approvalResponse = await apiService.approveLightSpotRequest(selectedAccount.id, selectedMentor)
      console.log('Approval Response:', approvalResponse)
      
      // Then assign the student to the mentor
      if (approvalResponse.student_id) {
        console.log('Assigning student to mentor:', approvalResponse.student_id, selectedMentor)
        await apiService.assignStudentToMentor(approvalResponse.student_id, selectedMentor)
      }
      
      setSuccessMessage("Account approved and mentor assigned successfully!")
      
      // Refresh the accounts list
      console.log('Refreshing accounts list...')
      const updatedAccounts = await apiService.getLightSpotRequests()
      console.log('Updated Accounts List:', updatedAccounts)
      
      // Find the updated account to verify mentor assignment
      const updatedAccount = updatedAccounts.find(acc => acc.id === selectedAccount.id)
      console.log('Updated Account after mentor assignment:', updatedAccount)
      
      setAccounts(updatedAccounts)
      
      // Close the form
      setShowAssignForm(false)
      setSelectedAccount(null)
      setSelectedMentor("")
    } catch (error) {
      console.error('Error approving account and assigning mentor:', error)
      setError(error.message || 'Failed to approve account and assign mentor')
    } finally {
      setIsLoading(false)
    }
  }

  const approveAccount = async (id) => {
    try {
      setIsLoading(true)
      setError("")
      
      // Get the account data first
      const account = await apiService.getLightSpotRequestById(id)
      if (account) {
        setSelectedAccount(account)
        setShowAssignForm(true)
        setShowDetails(false) // Hide details view if it's open
      }
    } catch (error) {
      console.error('Error loading account:', error)
      setError(error.message || 'Failed to load account')
    } finally {
      setIsLoading(false)
    }
  }

  const rejectAccount = async (id) => {
    try {
      setIsLoading(true)
      setError("")
      await apiService.rejectLightSpotRequest(id)
      setSuccessMessage("Account rejected and removed successfully!")
      await loadAccounts()
    } catch (error) {
      console.error('Error rejecting account:', error)
      setError(error.message || 'Failed to reject account')
    } finally {
      setIsLoading(false)
    }
  }

  const viewAccountDetails = async (id) => {
    try {
      setIsLoading(true)
      setError("")
      const account = await apiService.getLightSpotRequestById(id)
      setSelectedAccount(account)
      setShowDetails(true)
    } catch (error) {
      console.error('Error loading account details:', error)
      setError(error.message || 'Failed to load account details')
    } finally {
      setIsLoading(false)
    }
  }

  const filteredAccounts = accounts.filter((account) => {
    const matchesStatus = filterStatus === "all" || account.status === filterStatus
    const matchesLevel = filterLevel === "all" || (account?.level || 'basic') === filterLevel
    const matchesSearch = searchQuery === "" || 
      account.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.email.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesStatus && matchesLevel && matchesSearch
  })

  return (
    <div className="light-spot-accounts-page">
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control"
          />
        </div>

        <div className="filter-box">
          <select value={filterStatus} onChange={handleStatusFilterChange} className="form-control">
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="filter-box">
          <select value={filterLevel} onChange={handleLevelFilterChange} className="form-control">
            <option value="all">All Levels</option>
            <option value="basic">Basic</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
      </div>

      {showAssignForm && selectedAccount && (
        <div className="form-container">
          <h2 className="form-title">Assign Mentor to {selectedAccount.name}</h2>
          <form onSubmit={assignMentor}>
            <div className="form-group">
              <label htmlFor="mentor">Select Mentor</label>
              <select
                id="mentor"
                className="form-control"
                value={selectedMentor}
                onChange={handleMentorChange}
                required
                disabled={isLoading}
              >
                <option value="">-- Select a mentor --</option>
                {mentors.map((mentor) => (
                  <option key={mentor.id} value={mentor.id}>
                    {mentor.username}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAssignForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn" disabled={isLoading}>
                {isLoading ? "Assigning..." : "Assign Mentor"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showDetails && selectedAccount ? (
        <div className="account-details">
          <button className="btn btn-secondary" onClick={() => setShowDetails(false)}>
            <i className="material-icons">arrow_back</i> Back to List
          </button>
          
          <div className="account-details-card card">
            <div className="account-header">
              <h3 className="account-name">{selectedAccount.name}</h3>
              <span className={`status-badge status-${selectedAccount.status}`}>
                {selectedAccount.status.charAt(0).toUpperCase() + selectedAccount.status.slice(1)}
              </span>
            </div>

            <div className="account-content">
              <div className="detail-group">
                <label>Email:</label>
                <p>{selectedAccount.email}</p>
              </div>
              <div className="detail-group">
                <label>Phone:</label>
                <p>{selectedAccount.phone}</p>
              </div>
              <div className="detail-group">
                <label>Country:</label>
                <p>{selectedAccount.country}</p>
              </div>
              <div className="detail-group">
                <label>Level:</label>
                <p>{(selectedAccount?.level || 'basic').charAt(0).toUpperCase() + (selectedAccount?.level || 'basic').slice(1)}</p>
              </div>
              <div className="detail-group">
                <label>Request Date:</label>
                <p>{new Date(selectedAccount.created_at).toLocaleDateString()}</p>
              </div>
              <div className="detail-group">
                <label>About Me:</label>
                <p>{selectedAccount.about_me}</p>
              </div>
              <div className="detail-group">
                <label>Recommended By:</label>
                <p>{selectedAccount.recommended_by}</p>
              </div>
              {selectedAccount.mentor && (
                <div className="detail-group">
                  <label>Assigned Mentor:</label>
                  <p>{selectedAccount.mentor.username}</p>
                </div>
              )}
            </div>

            <div className="account-actions">
              {selectedAccount.status === "pending" && (
                <>
                  <button className="btn btn-sm" onClick={() => approveAccount(selectedAccount.id)}>
                    <i className="material-icons">check_circle</i> Approve
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => rejectAccount(selectedAccount.id)}>
                    <i className="material-icons">cancel</i> Reject
                  </button>
                </>
              )}
              {selectedAccount.status === "approved" && !selectedAccount.mentor && (
                <button className="btn btn-sm" onClick={() => openAssignForm(selectedAccount)}>
                  <i className="material-icons">person_add</i> Assign Mentor
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <h2 className="section-title">Light Spot Account Requests</h2>

          {isLoading ? (
            <div className="loading">Loading accounts...</div>
          ) : filteredAccounts.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Level</th>
                  <th>Request Date</th>
                  <th>Status</th>
                  <th>Mentor</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr key={account.id}>
                    <td>{account.name}</td>
                    <td>{account.email}</td>
                    <td>
                      <span className={`level-badge level-${account?.level || 'basic'}`}>
                        {(account?.level || 'basic').charAt(0).toUpperCase() + (account?.level || 'basic').slice(1)}
                      </span>
                    </td>
                    <td>{new Date(account.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge status-${account.status}`}>
                        {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      {(() => {
                        console.log('Rendering mentor for account:', account.id, 'Mentor data:', account.mentor)
                        return account.mentor ? account.mentor.username : "Not Assigned"
                      })()}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn" title="View Details" onClick={() => viewAccountDetails(account.id)}>
                          <i className="material-icons">visibility</i>
                        </button>
                        {account.status === "pending" && (
                          <>
                            <button className="action-btn" title="Approve" onClick={() => approveAccount(account.id)}>
                              <i className="material-icons">check_circle</i>
                            </button>
                            <button className="action-btn" title="Reject" onClick={() => rejectAccount(account.id)}>
                              <i className="material-icons">cancel</i>
                            </button>
                          </>
                        )}
                        {account.status === "approved" && !account.mentor && (
                          <button className="action-btn" title="Assign Mentor" onClick={() => openAssignForm(account)}>
                            <i className="material-icons">person_add</i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-results">
              <p>No accounts found matching your filter criteria.</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default LightSpotAccounts
